import { Component, OnInit } from '@angular/core';
import { TextFieldBaseComponent } from 'src/app/base/components/fields/textfield-base.component';

@Component({
  selector: '[app-easysuite-textfield]',
  templateUrl: './easysuite-textfield.component.html',
  styleUrls: ['./easysuite-textfield.component.css']
})
export class TextfieldComponent extends TextFieldBaseComponent implements OnInit {

  constructor() {super()}

  ngOnInit(): void {
    if (this.field.format === 'price') {
      this.formatInputValue(this.fieldKey);
    }
  }

  /**
   * Avoid alphabetic characters in numeric and price fields
   */
  avoidAlphaCharacters(event: KeyboardEvent): void {
    const charCode = (event.which) ? event.which : event.keyCode;
    const allowedCodes = [8, 9, 13, 46, 110, 190];
    const isNumber = charCode >= 48 && charCode <= 57;
    const isAllowed = allowedCodes.includes(charCode);

    if (!isNumber && !isAllowed) {
      event.preventDefault();
    }
  }

  /**
 * Handle live formatting of a numeric input with thousands separators and
 * up to two decimal places, preserving the cursor position after re-format.
 */
  onNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart ?? 0;
    const oldValue = input.value || '';

    // Remove commas and any non-digit/non-dot characters.
    let rawValue = oldValue.replace(/,/g, '').replace(/[^0-9.]/g, '');

    // Keep only the first dot to prevent multiple decimal points.
    const firstDotIndex = rawValue.indexOf('.');
    if (firstDotIndex !== -1) {
      rawValue = rawValue.slice(0, firstDotIndex + 1)
          + rawValue.slice(firstDotIndex + 1).replace(/\./g, '');
    }

    const parts = rawValue.split('.');
    const integerPart = parts[0] || '';

    // Limit decimal to 2 places.
    const decimalPart = (parts[1] || '').slice(0, 2);

    // Format integer part with thousands separators.
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const newValue = decimalPart.length ? `${formattedInteger}.${decimalPart}` : formattedInteger;

    // Count digits to the left of the old cursor to restore the position after formatting.
    const valueLeftOfCursor = oldValue.slice(0, selectionStart);
    const digitCountLeftOfCursor = valueLeftOfCursor.replace(/[^0-9]/g, '').length;

    let newCursorPosition = newValue.length;
    if (digitCountLeftOfCursor > 0) {
        let digitsSeen = 0;
        for (let i = 0; i < newValue.length; i++) {
            if (/\d/.test(newValue[i])) digitsSeen++;
            if (digitsSeen === digitCountLeftOfCursor) {
                newCursorPosition = i + 1;
                break;
            }
        }
    } else {
        newCursorPosition = 0;
    }

    // Apply the formatted value and update the form control without emitting events.
    input.value = newValue;
    const fieldControl = this.formGroup.get(this.fieldKey);
    if (fieldControl) fieldControl.setValue(newValue, { emitEvent: false });

    // Restore the cursor position after DOM update.
    try {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
    } catch (_e) {
        // Some browsers don't support setSelectionRange on certain input types.
    }
  }

  /**
   * Format price input value to two decimal places
   */
  formatInputValue(fieldKey: string): void {
    const control = this.formGroup.get(fieldKey);
    
    let currentValue = String(control.value || '');
    currentValue = currentValue.replace(/,/g, '');

    if (currentValue === '') {
      control.setValue('', { emitEvent: false });
      return;
    }

    const numericValue = parseFloat(currentValue);
    const roundedValue = Math.round(numericValue * 100) / 100;

    const formattedValue = roundedValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    control.setValue(formattedValue, { emitEvent: false });
  }

  /**
   * Check if the field requires numeric validation
   */
  shouldValidateNumeric(): boolean {
    return this.field.format === 'number' || this.field.format === 'price';
  }

  /**
   * Check if the field should format as price
   */
  shouldFormatPrice(): boolean {
    return this.field.format === 'price';
  }

}
